import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AppGuard, AuthRedirect, PublicOverviewGuard } from 'guards';
import { IframeGuard } from 'guards/IframeGuard';
import { LandingPage, MainPage, SettingPage, SignInPage, SignUpPage, VerifyEmailPage } from 'pages';
import AddRequest from 'pages/main/client-pool/AddRequest';
import CompetitorsTab from 'pages/main/competitors';
import MainContextProvider from 'pages/MainProvider';
import NotificationPage from 'pages/notification';
import OnboardingPage from 'pages/on-boarding';
import SubscriptionCanceled from 'pages/on-boarding/SelectPlan/Cancel';
import SubscriptionSuccess from 'pages/on-boarding/SelectPlan/Success';
import EmailUpdatedPage from 'pages/setting/account/EmailUpdated';
import CheckViewShareLinkSection from 'pages/viewsharelink/check';
import IframePage from 'pages/IframePage';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import useUserStore from 'store/useUserStore';
import { ROUTES } from 'util/Constants';
import { getTokenFromLocalStorage } from 'util/localStorageHelper';
import { FreePlanUpgradeProvider } from 'components/Providers/FreePlanUpgradeProvider';
import PublicOverviewPage from 'pages/PublicOverviewPage';
import CenterLoader from 'components/CenterLoader';

export const queryClient = new QueryClient();

const NotFoundRedirect = () => {
    const { userInfo, isOnboardingUser, fetchUser } = useUserStore();
    const userToken = getTokenFromLocalStorage();

    // If there's no token, redirect to public overview
    if (!userToken) {
        return <Navigate to={ROUTES.PUBLIC_OVERVIEW} replace />;
    }

    // If there's a token, fetch user data
    const { isLoading } = useQuery({
        queryKey: ['fetchUser'],
        queryFn: fetchUser,
        enabled: !!userToken && userInfo === null,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
    });

    // If loading, show loading page
    if (isLoading) {
        return <CenterLoader />;
    }

    // If we have userInfo, redirect based on user status
    if (userInfo) {
        if (isOnboardingUser()) {
            return <Navigate to={ROUTES.ONBOARDING} replace />;
        }
        return <Navigate to={ROUTES.MAIN} replace />;
    }

    // Fallback to public overview
    return <Navigate to={ROUTES.PUBLIC_OVERVIEW} replace />;
};

const RootRedirect = () => {
    const { userInfo, isOnboardingUser, fetchUser } = useUserStore();
    const userToken = getTokenFromLocalStorage();

    // If there's no token, redirect to public overview
    if (!userToken) {
        return <Navigate to={ROUTES.PUBLIC_OVERVIEW} replace />;
    }

    // If there's a token, fetch user data
    const { isLoading } = useQuery({
        queryKey: ['fetchUser'],
        queryFn: fetchUser,
        enabled: !!userToken && userInfo === null,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
    });

    // If loading, show loading page
    if (isLoading) {
        return <CenterLoader />;
    }

    // If we have userInfo, redirect based on user status
    if (userInfo) {
        if (isOnboardingUser()) {
            return <Navigate to={ROUTES.ONBOARDING} replace />;
        }
        return <Navigate to={ROUTES.MAIN} replace />;
    }

    // Fallback to public overview
    return <Navigate to={ROUTES.PUBLIC_OVERVIEW} replace />;
};

const routeConfig = [
    { path: '*', element: <NotFoundRedirect /> },
    {
        path: '/',
        element: <RootRedirect />,
    },
    { path: ROUTES.LADNING, element: <LandingPage /> },
    { path: ROUTES.VERIFY_EMAIL, element: <VerifyEmailPage /> },
    {
        path: ROUTES.PUBLIC_OVERVIEW,
        element: (
            <PublicOverviewGuard>
                <PublicOverviewPage />
            </PublicOverviewGuard>
        ),
    },

    {
        path: ROUTES.SIGN_IN,
        element: (
            <AuthRedirect>
                <SignInPage />
            </AuthRedirect>
        ),
    },
    {
        path: ROUTES.SIGN_UP,
        element: (
            <AuthRedirect>
                <SignUpPage />
            </AuthRedirect>
        ),
    },
    {
        path: ROUTES.VIEWSHARELINK,
        element: <CheckViewShareLinkSection />,
    },
    {
        path: ROUTES.NOTIFICATIONS,
        element: (
            <AppGuard>
                <NotificationPage />
            </AppGuard>
        ),
    },
    {
        path: ROUTES.ONBOARDING,
        element: (
            <AppGuard>
                <OnboardingPage />
            </AppGuard>
        ),
    },
    {
        path: ROUTES.MAIN,
        element: (
            <AppGuard>
                <MainPage />
            </AppGuard>
        ),
    },
    {
        path: ROUTES.COMPETITORS,
        element: (
            <AppGuard>
                <CompetitorsTab />
            </AppGuard>
        ),
    },
    {
        path: ROUTES.SETTING,
        element: (
            <AppGuard>
                <SettingPage />
            </AppGuard>
        ),
    },
    {
        path: ROUTES.ADD_REQUEST,
        element: (
            <AppGuard>
                <AddRequest />
            </AppGuard>
        ),
    },
    { path: ROUTES.SUBSCRIPTION_SUCCESS, element: <SubscriptionSuccess /> },
    { path: ROUTES.SUBSCRIPTION_CANCELED, element: <SubscriptionCanceled /> },
    { path: ROUTES.EMAIL_UPDATED, element: <EmailUpdatedPage /> },
];

const AppRoutes = () => {
    const isPremiumUser = useUserStore((state) => state.isPremiumUser());
    const isFreePlan = !isPremiumUser;

    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <MainContextProvider>
                    <FreePlanUpgradeProvider isFreePlan={isFreePlan}>
                        <Routes>
                            {routeConfig.map(({ path, element }, index) => (
                                <Route key={index} path={path} element={element} />
                            ))}
                        </Routes>
                    </FreePlanUpgradeProvider>
                </MainContextProvider>
            </BrowserRouter>
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    );
};

export default AppRoutes;
